import { useState, type FormEvent } from "react";
import { Mail, Check, Loader2 } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const emailSchema = z
  .string()
  .trim()
  .min(1, "Please enter your email.")
  .max(255, "Email is too long.")
  .email("Please enter a valid email address.");

type Status = "idle" | "submitting" | "success";

export function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid email.");
      return;
    }
    setError(null);
    setStatus("submitting");

    const { error: insertError } = await supabase
      .from("newsletter_signups")
      .insert({ email: parsed.data.toLowerCase(), source: "footer" });

    // 23505 = already subscribed; treat as success for the visitor.
    if (insertError && insertError.code !== "23505") {
      setStatus("idle");
      setError("Something went wrong. Please try again.");
      return;
    }

    setStatus("success");
    setEmail("");
    toast.success("You're on the list!", {
      description: "We'll send Coffman Cove fishing news, rates, and open dates.",
    });
  }

  if (status === "success") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="rounded-2xl border border-[var(--electric)]/30 bg-[var(--electric)]/10 p-4"
      >
        <p className="flex items-center gap-2 text-sm font-semibold text-[var(--electric)]">
          <Check className="h-4 w-4" /> You're subscribed
        </p>
        <p className="mt-1 text-xs leading-relaxed text-[var(--offwhite-warm)]/80">
          Watch your inbox for trip openings, fishing reports, and lodge news from Coffman Cove.
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-3 text-xs font-medium text-[var(--electric)] underline-offset-2 hover:underline"
        >
          Subscribe another email
        </button>
      </div>
    );
  }

  const submitting = status === "submitting";

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-2">
      <label htmlFor="newsletter-email" className="sr-only">
        Email address
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Mail
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--electric)]"
            aria-hidden
          />
          <input
            id="newsletter-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "newsletter-error" : "newsletter-hint"}
            disabled={submitting}
            maxLength={255}
            className="min-h-11 w-full rounded-full border border-[var(--electric)]/25 bg-[var(--navy)]/60 pl-9 pr-3 py-2.5 text-sm text-[var(--offwhite)] placeholder:text-[var(--offwhite-warm)]/50 outline-none transition-colors focus:border-[var(--electric)] focus:ring-2 focus:ring-[var(--electric)]/30 disabled:opacity-60"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-[var(--electric)] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--electric-foreground)] shadow-[var(--shadow-electric)] transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
        >
          {submitting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Joining…
            </>
          ) : (
            "Subscribe"
          )}
        </button>
      </div>
      {error ? (
        <p id="newsletter-error" role="alert" className="text-xs text-red-300">
          {error}
        </p>
      ) : (
        <p id="newsletter-hint" className="text-xs text-[var(--offwhite-warm)]/60">
          Occasional updates. No spam — unsubscribe anytime.
        </p>
      )}
    </form>
  );
}
